import {
  Button,
  Center,
  createStyles,
  Divider,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  useMantineTheme,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { ITrackedEquipment, ITrackedEquipmentClean } from '../../../../models';
import {
  TrackedEquipmentPatientSection,
  TrackedEquipmentDetailSection,
  Th,
  InfiniteScrollArea,
} from '../../../../components';
import { ArrowLeft, Search, Tool } from 'tabler-icons-react';
import { useEffect, useState } from 'react';
import {
  useIsAdmin,
  useIsTechnician,
  useTrackedEquipmentCleanByTrackedEquipment,
} from '../../../../hooks';
import dayjs from 'dayjs';

const useStyles = createStyles((theme) => ({
  notesCard: {
    border: `1px solid ${theme.colors.gray[4]}`,
    borderRadius: theme.radius.sm,
    paddingLeft: theme.spacing.sm,
    paddingRight: theme.spacing.sm,
    paddingTop: 3,
    width: '100%',
    height: '125px',
  },
  infoBox: {
    color: `${theme.colors.gray[6]}`,
    border: `1px solid ${theme.colors.gray[6]}`,
    borderRadius: theme.radius.lg,
    width: 'fit-content',
    paddingLeft: '10px',
    paddingRight: '10px',
  },
  groupLabel: {
    color: theme.colors.gray[5],
    fontSize: theme.fontSizes.sm,
    whiteSpace: 'pre',
  },
  fieldLabel: {
    color: theme.colors.gray[5],
    fontSize: theme.fontSizes.xs,
    whiteSpace: 'pre',
  },
  detail: {
    fontSize: theme.fontSizes.md,
    paddingLeft: 10,
  },
  fieldGroup: {
    width: '48%',
  },
  clickableIcon: {
    color: `${theme.colors.blue[6]}`,
    padding: '0 !important',
  },
  buttonText: {
    [`@media (max-width: ${theme.breakpoints.sm})`]: {
      fontSize: 12,
    },
  },
}));

function filterData(data: ITrackedEquipmentClean[], search: string) {
  if (data && data.length > 0) {
    const keys = Object.keys(data[0]);
    const query = search.toLowerCase().trim();
    return data.filter((item) =>
      keys.some((key) => (item[key] ?? '-').toString().toLowerCase().includes(query))
    );
  } else {
    return [];
  }
}

function sortData(
  data: ITrackedEquipmentClean[],
  payload: { sortBy: keyof ITrackedEquipmentClean; reversed: boolean; search: string }
) {
  if (!payload.sortBy) {
    return filterData(data, payload.search);
  }

  return filterData(
    [...data].sort((a, b) => {
      if (payload.reversed) {
        return (b[payload.sortBy] ?? '000ForceToBottom')
          .toString()
          .localeCompare((a[payload.sortBy] ?? '000ForceToBottom').toString());
      }

      return (a[payload.sortBy].toString() ?? '!!!ForceToTop').localeCompare(
        (b[payload.sortBy].toString() ?? '!!!ForceToTop').toString()
      );
    }),
    payload.search
  );
}

type TrackedEquipmentCleanHistoryModalProps = {
  trackedEquipment: ITrackedEquipment;
  cleanCreated: boolean;
  cleanDeleted: boolean;
  opened: boolean;
  onSelect: (clean: ITrackedEquipmentClean) => void;
  onClean: () => void;
  onClose: () => void;
};

const TrackedEquipmentCleanHistoryModal = (props: TrackedEquipmentCleanHistoryModalProps) => {
  const { classes } = useStyles();
  const theme = useMantineTheme();
  const isSmallMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);

  const isAdmin = useIsAdmin().data?.isAdmin;
  const isTechnician = useIsTechnician().data?.isTechnician;

  const cleanRecords = useTrackedEquipmentCleanByTrackedEquipment(
    props.trackedEquipment.trackedEquipmentId
  );

  const [search, setSearch] = useState('');
  const [sortedData, setSortedData] = useState(cleanRecords.data);
  const [sortBy, setSortBy] = useState<keyof ITrackedEquipmentClean>(null!);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);

  const setSorting = (field: keyof ITrackedEquipmentClean) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    setSortedData(sortData(cleanRecords.data!, { sortBy: field, reversed, search }));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setSearch(value);
    setSortedData(
      sortData(cleanRecords.data!, {
        sortBy,
        reversed: reverseSortDirection,
        search: value,
      })
    );
  };

  const rows = (sortedData ?? []).map((a: ITrackedEquipmentClean) => {
    return (
      <tr
        key={a.trackedEquipmentCleanId}
        style={{ cursor: 'pointer' }}
        onClick={() => {
          props.onSelect(a);
        }}
      >
        <td style={{ wordWrap: 'break-word', width: '25%' }}>{a.technicianNameFormatted}</td>
        <td style={{ wordWrap: 'break-word', width: '25%' }}>
          {dayjs(a.cleanDateTime).format('YYYY-MM-DD')}
        </td>
      </tr>
    );
  });

  useEffect(() => {
    if (!cleanRecords.data) {
      setSortedData(cleanRecords.data);
    } else {
      setSortedData(
        sortData(cleanRecords.data, { sortBy, reversed: reverseSortDirection, search })
      );
    }
  }, [cleanRecords.data, reverseSortDirection, search, sortBy]);

  useEffect(() => {
    if (props.opened) {
      cleanRecords.refetch();
    }
  }, [props.trackedEquipment, props.opened]);

  return (
    <Modal
      opened={props.opened}
      title={
        <Text style={{ color: 'green' }}>
          {props.cleanCreated
            ? 'Cleaning Record Successfully Created!'
            : props.cleanDeleted
            ? 'Cleaning Record Successfully Deleted!'
            : ''}
        </Text>
      }
      size={isSmallMobile ? 'xs' : 'md'}
      onClose={() => {
        props.onClose();
      }}
    >
      <Stack spacing={0} style={{ position: 'relative' }}>
        <Center>
          <Text size="xl" weight={500} align="center">
            Equipment Cleaning
          </Text>
        </Center>
        {props.trackedEquipment.checkedOutDate && !props.trackedEquipment.outForRepairs && (
          <TrackedEquipmentPatientSection
            trackedEquipment={props.trackedEquipment}
            header="Current Patient"
          />
        )}
        <TrackedEquipmentDetailSection trackedEquipment={props.trackedEquipment} readOnly />
        <Center mt={theme.spacing.xs}>
          <Text size="md" weight={500} align="center">
            Cleaning History
          </Text>
        </Center>
        <Divider />
        <TextInput
          placeholder="Search by any field"
          my="xs"
          icon={<Search size={14} />}
          value={search}
          onChange={handleSearchChange}
        />
        <Divider />
        <Table style={{ width: '100%', tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <Th
                sorted={sortBy === 'technicianNameFormatted'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('technicianNameFormatted')}
                style={{ width: '45%' }}
              >
                Technician
              </Th>
              <Th
                sorted={sortBy === 'cleanDateTime'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('cleanDateTime')}
                style={{ width: '45%' }}
              >
                Cleaned On
              </Th>
            </tr>
          </thead>
        </Table>
        <InfiniteScrollArea rows={rows} height={250} />
        <Divider />
        <Group spacing="xs" grow noWrap mt="xs">
          <Button
            variant="outline"
            p={0}
            onClick={() => {
              props.onClose();
            }}
          >
            <ArrowLeft style={{ marginRight: 5 }} />
            Return to Details
          </Button>
          {(isAdmin || isTechnician) && (
            <Button
              variant="outline"
              disabled={
                !props.trackedEquipment.readyForClean ||
                props.trackedEquipment.outForRepairs ||
                props.trackedEquipment.retired ||
                (props.trackedEquipment.checkedOutDate !== null &&
                  props.trackedEquipment.checkedOutDate !== undefined)
              }
              p={0}
              onClick={() => {
                props.onClean();
              }}
            >
              <Tool style={{ marginRight: 5 }} />
              Clean Equipment
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
};

export default TrackedEquipmentCleanHistoryModal;
